<template>
  <section class="download-faq container" aria-labelledby="download-faq-heading">
    <h2 id="download-faq-heading" class="download-faq__heading">FAQ секция</h2>
    <ul class="download-faq__list" role="list">
      <li
        v-for="(item, index) in faqItems"
        :key="index"
        class="download-faq__item"
        :class="{ 'download-faq__item--open': openIndex === index }"
      >
        <button
          type="button"
          class="download-faq__trigger"
          :aria-expanded="openIndex === index"
          :aria-controls="`faq-panel-${index}`"
          :id="`faq-trigger-${index}`"
          @click="toggle(index)"
        >
          <span class="download-faq__trigger-text">{{ item.title }}</span>
          <span class="download-faq__chevron" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
        </button>
        <div
          :id="`faq-panel-${index}`"
          class="download-faq__panel"
          role="region"
          :aria-labelledby="`faq-trigger-${index}`"
          :aria-hidden="openIndex !== index"
          :style="{ maxHeight: openIndex === index ? panelHeights[index] + 'px' : '0px' }"
        >
          <div class="download-faq__answer" v-html="item.answer" :ref="(el) => setAnswerRef(el, index)"></div>
        </div>
      </li>
    </ul>
  </section>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref } from 'vue';

const openIndex = ref(null);
const panelHeights = reactive({});
const answerEls = [];
let resizeObserver = null;

function setAnswerRef(el, index) {
  answerEls[index] = el;
  if (el) {
    panelHeights[index] = el.scrollHeight;
    resizeObserver?.observe(el);
  }
}

function toggle(index) {
  openIndex.value = openIndex.value === index ? null : index;
}

// The answer text is always in the DOM (only visually clipped via max-height),
// so a ResizeObserver keeps its measured height correct even when it changes
// after the initial measurement — e.g. once the Rubik webfont finishes loading
// and reflows the text into more/fewer lines than the fallback font showed.
onMounted(() => {
  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const index = answerEls.indexOf(entry.target);
      if (index !== -1) {
        panelHeights[index] = entry.target.scrollHeight;
      }
    }
  });
  answerEls.forEach((el) => el && resizeObserver.observe(el));
});

onUnmounted(() => resizeObserver?.disconnect());


const faqItems = [
  {
    title: 'Какво представлява приложението Momfident?',
    answer:
      'Momfident е виртуален психологически асистент, създаден с помощта на изкуствен интелект и научни проучвания, проверени от психолози и социални учени. Целта му е да предоставя достоверни съвети по лесен и достъпен начин по теми като емоционалното ви състояние, грижа за себе си, родителство, партньорски отношения, новата идентичност като майката, емоционалната връзка с детето и много други.',
  },
  {
    title: 'Каква е целта на психологическия асистент?',
    answer:
      'Асистентът е тук, за да ви информира, подкрепя и помага в грижата за вашето психично здраве. Той не замества работата с терапевт, но може да ви предостави проверена информация, стратегии за справяне и примери от други майки или клинични проучвания. Ако е нужна професионална помощ, ще насочи вниманието ви към това и ще предложи доверени специалисти и ресурси, към които можете да се обърнете.',
  },
  {
    title: 'С кого си говоря? / Momfident реален човек ли е или изкуствен интелект?',
    answer:
      'Макар да звучи плашещо или неразбираемо, използваме изкуствен интелект, за да обработваме голям обем информация по теми, свързани с психичното здраве на майките – с единствената цел асистентът да разбира по-добре въпросите ви и да дава отговори, базирани на проверена информация от научни източници, подбрани и одобрени от психолози и изследователи. Обучен е да води разговор, като задава въпроси, разбира проблема и предлага подходящо решение. Асистентът се развива постоянно благодарение на всяка обратна връзка, която получава.',
  },
  {
    title: 'Защо е нужен такъв асистент?',
    answer:
      'След стотици интервюта с майки и специалисти разбрахме, че майките имат нужда от безопасно пространство, в което да споделят как се чувстват – без страх от осъждане, с емоционална валидация и достоверна информация. Онлайн съдържанието често е объркващо или противоречиво, затова създадохме Momfident – за да ви изслуша, разбере, подкрепи и даде съвет без оценки, на разположение за вас 24/7 в анонимна и безопасна среда.',
  },
  {
    title: 'Какво ви дава приложението?',
    answer:
      'Безопасно и анонимно пространство, в което да се самоанализирате, без да изпитвате страх от осъждане.<br/>Персонализирани разговори, базирани на вашето емоционално състояние<br/>Съвети и ресурси за самопомощ, базирани на проучвания<br/>Емоционална валидация, стратегии за справяне и успешни примери от други жени<br/>Ранен скрининг и връзка със специалисти (при нужда)',
  },
  {
    title: 'Защо е важно да дам обратна връзка?',
    answer:
      'Вашата обратна връзка ни помага да направим Momfident по-полезен и близък до реалните нужди на вас и други майки като вас. Всяка обратна връзка служи за подобряване на модела, за набора на нови проучвания по теми и въпроси, които ви вълнуват. Така изграждаме инструмент за самопомощ, базиран на вашите реални потребности.',
  },
  {
    title: 'За кого е предназначена тази помощ?',
    answer:
      'Momfident е създаден основно за майки в първите три години след раждането, но може да бъде полезен и за други родители или хора, които се грижат за малки деца. Целта ни е да добавяме нови теми според въпросите и предизвикателствата в този период, а в бъдеще и теми като бременност, фертилност, менопауза и други, свързани с психичното здраве и благополучието на жените.',
  },
  {
    title: 'Може ли да имам доверие на информацията, която ми дава?',
    answer:
      'Да. Използваме само информация от научни публикации (издания с отворен достъп реферирани в научни бази като Google Scholar, EBSCO, Web of Science и Scopus), които са прегледани и одобрени от нашите социални учени и практикуващи психолози. Пълен списък с източници може да достъпите тук: <a href="https://momfident.io/research-references" target="_blank" rel="noopener noreferrer">https://momfident.io/research-references</a>',
  },
  {
    title: 'На какви теми мога да водя разговор?',
    answer:
      'Може да говорите за всичко, което ви вълнува – емоции, тревожност, умора, вина, самота, кърмене, сън на бебето, отношения с партньора, трудности във връзката с детето, промени в идентичността или баланса между личното време и майчинството. Momfident е тук, за да ви изслуша и подкрепи – без осъждане.',
  },
  {
    title:
      'Може ли асистентът да ме диагностицира за следродилна депресия или да замени нуждата от терапия?',
    answer:
      'Не. Momfident не замества терапия или медицинска помощ. Той може да помогне с ранно разпознаване на симптоми и да ви насочи към търсенето на специализирана подкрепа, ако е необходимо.',
  },
  {
    title: 'Безопасно ли е да споделям лична информация?',
    answer:
      'Да. Ние се придържаме стриктно към анонимизирането и защитата на личните ви данни. Информацията, която споделяте с асистента, е строго поверителна и се използва единствено за обучаване на нашия асистент, без това да бъде асоциирано с вас като личност. Разговорите ви се съхраняват, но те са анонимизирани и имат необходимите защити, за да останат недосегаеми. Повече информация: <a href="https://momfident.io/terms-and-conditions" target="_blank" rel="noopener noreferrer">https://momfident.io/terms-and-conditions</a>',
  },
  {
    title: 'Мога ли да говоря с истински специалист през приложението?',
    answer:
      'Асистентът е разработен и инструктиран внимателно съвместно с психолози и има нужните защити, за да разпознава кога е необходима работа със специалист. В такива случаи той насочва към доверени специалисти, с които работим. Към момента връзката с тях не се осъществява през самото приложение. В приложението са включени и валидирани скринингови скали за оценка и проследяване на вашето състояние.',
  },
  {
    title: 'Приложението безплатно ли е?',
    answer:
      'Приложението има безплатен период на използване до 2 седмици или 100 съобщения, или по-удължен период и брой съобщения при наличието на промокод от някой от нашите партньори. При изчерпването на периода или броя съобщения ще бъдете известени и ще ви бъдат предложени опции за едномесечен, тримесечен и шестмесечен период на използване. В приложението обаче остават като безплатен достъп психологически факти, видеа и статии от проверени източници или доверени специалисти, с които работим, както и достъп до скрининговите тестове.<br/><br/>С закупуването на абонамент за приложението Вие подкрепяте не само себе си, но и развитието на достоверен източник на самопомощ за други майки, които имат нужда от подкрепа.',
  },
  {
    title: 'Как мога да прекратя абонамента си?',
    answer:
      'Ако сте активирали абонамент през Apple App Store или Google Play, можете да го прекратите по всяко време от настройките на Вашия акаунт в съответната платформа. Изтриването на приложението не прекратява автоматично абонамента. След прекратяване ще запазите достъп до платения период до края на вече заплатения срок. В App Store това обикновено става от Settings &gt; [Вашето име] &gt; Subscriptions &gt; [Име на приложението] &gt; Cancel Subscription, а в Google Play от Google Play &gt; Subscriptions &gt; [Име на приложението] &gt; Cancel subscription.',
  },
  {
    title: 'Кога трябва да потърся помощ при симптоми на следродилна тревожност или депресия?',
    answer:
      'Състоянията след раждане могат да варират от леки до тежки. Ако усещате няколко симптома едновременно и продължително – като тъга, отчаяние, гадене, промени в съня или апетита, липса на желание за интимност, чести плачове, тревожност, раздразнителност, усещане за изолация, силни емоционални колебания, мисли за самонараняване или нараняване на бебето, включително мисли за самоубийство – незабавно се обърнете към специалист или кризисен център.',
  },
  {
    title: 'Къде да потърся помощ?',
    answer:
      'Ако имате съмнения за следродилна депресия или симптомите ви се засилват, не се колебайте да се обърнете към специалист.<br/>📞 Спешна психотерапевтична помощ и емоционална подкрепа:<br/>kabinet.bg – онлайн консултации с психолози и психотерапевти<br/>Телефон на доверието: 0800 20 202 (анонимна и безплатна подкрепа)<br/><br/> Национална асоциация „Подкрепа за кърмене“<br/>www.podkrepazakarmene.com<br/>Телефон за контакт: 0896 419 488<br/><br/> Психотерапевти<br/>Регистър на психотерапевтите към Българска асоциация по психотерапия 🔗 <a href="https://psychotherapy-bg.org/registar-na-psihoterapevtite" target="_blank" rel="noopener noreferrer">psychotherapy-bg.org/registar-na-psihoterapevtite</a><br/><br/> Психолози<br/>Регистър на психолозите към Дружеството на психолозите в Република България 🔗 <a href="https://psychology-bg.org/new-register/register" target="_blank" rel="noopener noreferrer">psychology-bg.org/new-register/register</a><br/><br/>🩺 Психиатри<br/>Списък с психиатри към Български лекарски съюз 🔗 <a href="https://blsbg.eu/bg/medics/search" target="_blank" rel="noopener noreferrer">blsbg.eu/bg/medics/search</a><br/><br/> Фондация "Асоциация Анимус"<br/><a href="https://animusassociation.org" target="_blank" rel="noopener noreferrer">animusassociation.org</a><br/>Гореща линия за пострадали от насилие: 📞 +359 2 983 5205 📞 +359 2 983 5405<br/><br/>За пълния списък от източници на подкрепа и за връзка с някой от нашите доверени специалисти - вижте тук: <a href="https://momfident.io/about" target="_blank" rel="noopener noreferrer">https://momfident.io/about</a>',
  },
  {
    title: 'Как да разбера дали това, което чувствам след раждането, е нормално?',
    answer:
      'Можете да попитате асистента. Той ще ви помогне да разграничите кое е често срещано, кога е добре да обърнете внимание и дали да потърсите помощ, ако е нужно.',
  },
  {
    title: 'Мога ли да задам въпроси за връзката с партньора си и как майчинството я променя?',
    answer:
      'Да. Можете да говорите за комуникация, интимност, конфликти и как майчинството влияе на партньорските отношения. Един от основните ни модули е именно партньорски отношения.',
  },
  {
    title: 'Мога ли да разчитам на Momfident, когато ми е тежко вечер или се чувствам сама?',
    answer:
      'Можете да го използвате по всяко време, 24/7, за да споделите как се чувствате, да получите насоки и усещане за подкрепа, дори когато няма с кого да поговорите или се чувствате неразбрани. Това не означава да не търсите подкрепа и разбиране от близките около вас, а да имате защитено пространство за саморефлексия и разговор със себе си.',
  },
  {
    title: 'Как мога да изтрия акаунта си?',
    answer:
      'За да изтриете акаунта си, можете да кликнете <a href="https://momfident.io/account/request-delete" target="_blank" rel="noopener noreferrer" style="color: #FC678B; text-decoration: underline; cursor: pointer;">тук</a>.',
  },
];

</script>

<style scoped lang="scss">
@import "~@/assets/scss/fonts.scss";
@import "~@/assets/scss/variables.scss";

.download-faq {
  font-family: 'Rubik', sans-serif;
  background: #fff;
  padding-top: 28px;
  padding-bottom: 56px;
  max-width: 960px;
}

.download-faq__heading {
  font-family: $font-thin;
  font-weight: 300;
  line-height: 1;
  letter-spacing: 0;
  color: #5a5583;
  text-align: center;
  margin: 0 0 3rem 0;
  padding-top: 40px;
}

.download-faq__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.download-faq__item {
  margin: 0;
  background-color: #fbe9ec;
  /* Fixed radius at all times — animating pill → rounded clipped answer text */
  border-radius: 24px;
  overflow: hidden;
}

.download-faq__trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 22px;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  color: $color-purple;
  font-family: inherit;
  font-size: 0.95rem;
  font-weight: 400;
  line-height: 1.45;
  letter-spacing: 0;
  transition: color 0.2s ease;

  &:hover {
    color: darken($color-purple, 8%);
  }

  &:focus-visible {
    outline: 2px solid $color-pink-dark-2;
    outline-offset: 2px;
  }
}

.download-faq__trigger-text {
  flex: 1;
  min-width: 0;
}

.download-faq__chevron {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $color-purple;
  transition: transform 0.32s linear;

  .download-faq__item--open & {
    transform: rotate(180deg);
  }
}

/* Height-only animation via JS-measured scrollHeight — max-height between two
   fixed px values transitions reliably across browsers (grid-template-rows: fr
   transitions don't animate consistently in every engine). */
.download-faq__panel {
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.36s linear;
}

.download-faq__answer {
  padding: 0 22px 18px;
  margin: 0;
  border-top: 1px solid rgba(107, 107, 156, 0.12);
  padding-top: 14px;
  color: $color-purple;
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.55;
  text-align: left;

  :deep(a) {
    color: $color-pink-dark;
    text-decoration: underline;
    text-underline-offset: 2px;
    word-break: break-all;

    &:hover {
      color: darken(#ea708c, 6%);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .download-faq__item,
  .download-faq__chevron,
  .download-faq__panel {
    transition-duration: 0.01ms;
  }
}

@media (max-width: 768px) {
  .download-faq {
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 40px;
  }

  .download-faq__heading {
    font-size: 24px;
    padding-top: 20px;
    margin-bottom: 30px;
  }

  .download-faq__trigger {
    font-size: 0.875rem;
    padding: 12px 18px;
  }

  .download-faq__answer {
    font-size: 0.82rem;
    padding: 12px 18px 16px;
    padding-top: 12px;
  }

  .download-faq__list {
    gap: 10px;
  }

  .download-faq__item {
    border-radius: 20px;
  }
}
</style>
